import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { usePortalQuery } from '../../hooks/usePortalQuery';
import { listNotifications, markAllNotificationsRead, markNotificationRead } from '../../lib/portalApi';
import { formatRelative } from '../../utils/portalFormat';
export function NotificationBell() {
  const {
    data
  } = usePortalQuery(() => listNotifications(), []);
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const items = data?.data ?? [];
  const unread = items.filter(n => !n.isRead).length;
  React.useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => !ref.current?.contains(event.target as Node) && setOpen(false);
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return <div ref={ref} className="relative" data-id="element-998" data-mp="1stjn04kynjjm">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} className="relative inline-flex h-10 w-10 items-center justify-center rounded-sm text-body transition-colors duration-150 hover:bg-panel hover:text-ink" data-id="element-999" data-mp="1sumcc82mxdxf">
        <BellIcon className="h-5 w-5" aria-hidden="true" data-id="element-1000" data-mp="2bsfhzi4n81yi" />
        {unread > 0 && <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-charcoal" data-id="element-1001" data-mp="26tm1rhxz1wun">
            {unread}
          </span>}
      </button>

      <AnimatePresence data-id="element-1002" data-mp="2zhq8m6sa4fyx">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute right-0 top-full z-40 mt-2 w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-md border border-line bg-panel shadow-2xl" data-id="element-1003" data-mp="2zgyq9xnp0eql">
            <div className="flex items-center justify-between border-b border-line px-4 py-3" data-id="element-1004" data-mp="3dm8s3ev210kn">
              <p className="text-[14px] font-medium text-ink" data-id="element-1005" data-mp="2zmft6w9dje1l">Notifications</p>
              {unread > 0 && <button type="button" onClick={() => void markAllNotificationsRead()} className="text-[13px] text-royal hover:underline" data-id="element-1006" data-mp="34oexq9guoh04">
                  Mark all as read
                </button>}
            </div>
            {items.length === 0 ? <p className="px-4 py-8 text-center text-[14px] text-muted" data-id="element-1007" data-mp="ox0v2dtwzhst">You are all caught up.</p> : <ul className="max-h-[60vh] divide-y divide-line overflow-y-auto" data-id="element-1008" data-mp="sx17xu1d7kxr">
                {items.map(n => <li key={n.id} data-id="element-1009" data-mp="2dditsxy50344">
                    <button type="button" onClick={() => {
              void markNotificationRead(n.id);
              setOpen(false);
              if (n.link) navigate(n.link);
            }} className="flex w-full gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-charcoal" data-id="element-1010" data-mp="29ejrll3sikgd">
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.isRead ? 'bg-transparent' : 'bg-gold'}`} aria-hidden="true" data-id="element-1011" data-mp="2s3gakstqlqja" />
                      <span className="min-w-0" data-id="element-1012" data-mp="3626h0z2g52kb">
                        <span className={`block text-[14px] ${n.isRead ? 'text-body' : 'font-medium text-ink'}`} data-id="element-1013" data-mp="314xzn0p4h2k4">{n.title}</span>
                        {n.body && <span className="mt-0.5 block text-[13px] text-muted" data-id="element-1014" data-mp="1bqhxskp6mdu4">{n.body}</span>}
                        <span className="mt-1 block text-[12px] text-muted" data-id="element-1015" data-mp="16szpmwizo2n6">{formatRelative(n.createdAt)}</span>
                      </span>
                      {!n.isRead && <span className="sr-only" data-id="element-1016" data-mp="1d6vqzgl55qzp">Unread</span>}
                    </button>
                  </li>)}
              </ul>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}